/**
 * localeController
 * 站点多语言（i18n）核心控制器。
 * 负责：语言规范化、语言持久化、URL 语言前缀的解析/构建、链接本地化、
 * 语言切换以及将控制器挂载到 window 供非 Vue 环境（如 CMS 注入脚本）调用。
 */

// 当前站点支持的语言列表（as const 用于推导出联合类型）
export const SUPPORTED_LOCALES = ['en', 'zh', 'ru', 'kk', 'ko', 'da', 'ja'] as const

// 支持的语言编码联合类型，如 'en' | 'zh' | ...
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

// 语言运行模式：storefront 为正式店铺前台，preview 为编辑器预览态
type LocaleRuntimeMode = 'storefront' | 'preview'

// resolveSiteHref / navigateSiteHref 等方法的可选参数
type ResolveSiteHrefOptions = {
  currentPath?: string // 当前路径（默认取 window.location）
  currentOrigin?: string // 当前源（默认取 window.location.origin）
  mainLanguage?: unknown // 站点主语言（主语言不加语言前缀）
}

// 控制器的运行时配置
type LocaleRuntimeConfig = {
  mainLanguage?: SupportedLocale | null // 站点主语言
}

// 当前路由的语言作用域信息
type RouteLocaleScope = {
  locale: SupportedLocale | null // 当前路径解析出的语言，无前缀时为 null
  hasLocalePrefix: boolean // 当前路径是否带有语言前缀
}

// 单个语言的元信息（用于语言切换菜单展示）
type LocaleMeta = {
  code: SupportedLocale // 语言编码
  label: string // 语言展示名称
  emoji: string // 国旗 emoji
}

// 各支持语言的元信息映射表
const LOCALE_META: Record<SupportedLocale, LocaleMeta> = {
  en: { code: 'en', label: 'English', emoji: '🇺🇸' },
  zh: { code: 'zh', label: '中文', emoji: '🇨🇳' },
  ru: { code: 'ru', label: 'Русский', emoji: '🇷🇺' },
  kk: { code: 'kk', label: 'Қазақша', emoji: '🇰🇿' },
  ko: { code: 'ko', label: '한국어', emoji: '🇰🇷' },
  da: { code: 'da', label: 'Dansk', emoji: '🇩🇰' },
  ja: { code: 'ja', label: '日本語', emoji: '🇯🇵' },
}

const DEFAULT_LOCALE: SupportedLocale = 'en' // 默认兜底语言
const STORAGE_KEY = 'site-lang' // localStorage 中存储语言的键名
const LOCALE_PREFIX_RE = /^\/([a-z]{2})(?=\/|$)/i // 匹配路径开头的两位语言前缀，如 /en 或 /zh/
// 运行时配置，通过 configureLocaleController 注入
const localeRuntimeConfig: LocaleRuntimeConfig = {
  mainLanguage: null,
}

// 判断当前是否在浏览器环境（SSR 下无 window）
const isBrowser = () => typeof window !== 'undefined'

/**
 * 将任意输入规范化为受支持的语言编码。
 * 支持大小写、下划线（如 zh_CN）以及带地区后缀（如 en-US）的输入，
 * 无法识别时返回默认语言。
 */
export const normalizeLocale = (value: unknown): SupportedLocale => {
  const raw = typeof value === 'string' ? value.trim().toLowerCase().replace(/_/g, '-') : ''
  if (!raw) return DEFAULT_LOCALE
  if (raw.startsWith('zh')) return 'zh'
  if (raw.startsWith('en')) return 'en'
  if (raw.startsWith('ru')) return 'ru'
  if (raw.startsWith('kk')) return 'kk'
  if (raw.startsWith('ko')) return 'ko'
  if (raw.startsWith('da')) return 'da'
  if (raw.startsWith('ja')) return 'ja'
  return DEFAULT_LOCALE
}

// 类型守卫：判断输入是否为精确受支持的语言编码（不做前缀近似匹配）
const isSupportedLocale = (value: unknown): value is SupportedLocale => {
  const raw = typeof value === 'string' ? value.trim().toLowerCase().replace(/_/g, '-') : ''
  return SUPPORTED_LOCALES.includes(raw as SupportedLocale)
}

// 获取某语言的元信息（编码、名称、国旗），输入会先被规范化
export const getLocaleMeta = (value: unknown): LocaleMeta => LOCALE_META[normalizeLocale(value)]

/**
 * 配置控制器的运行时参数（当前主要为站点主语言）。
 * 主语言的路径不会被添加语言前缀。返回最新的配置快照。
 */
export const configureLocaleController = (config: { mainLanguage?: unknown } = {}) => {
  if (config.mainLanguage !== undefined) {
    const rawMainLanguage = String(config.mainLanguage || '').trim()
    localeRuntimeConfig.mainLanguage = rawMainLanguage ? normalizeLocale(rawMainLanguage) : null
  }
  return {
    mainLanguage: localeRuntimeConfig.mainLanguage,
  }
}

// 解析实际生效的主语言：优先使用传入值，否则回退到运行时配置
const resolveMainLanguage = (value?: unknown): SupportedLocale | null => {
  if (value !== undefined && value !== null && String(value).trim()) return normalizeLocale(value)
  return localeRuntimeConfig.mainLanguage || null
}

// 将语言持久化到 localStorage，SSR 或写入失败时静默忽略
export const persistLocale = (value: unknown): SupportedLocale => {
  const locale = normalizeLocale(value)
  if (!isBrowser()) return locale
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // ignore storage failure
  }
  return locale
}

// 将语言写入 <html lang="...">，便于 SEO 与无障碍，SSR 时仅返回规范化结果
export const applyHtmlLang = (value: unknown): SupportedLocale => {
  const locale = normalizeLocale(value)
  if (!isBrowser()) return locale
  try {
    document.documentElement.lang = locale
  } catch {
    // ignore dom failure
  }
  return locale
}

// 读取 localStorage 中保存的语言，读取失败或 SSR 时返回默认语言
export const getStoredLocale = (): SupportedLocale => {
  if (!isBrowser()) return DEFAULT_LOCALE
  try {
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY) || '')
  } catch {
    return DEFAULT_LOCALE
  }
}

/**
 * 获取当前生效语言，按优先级依次尝试：
 * 1) URL 路径中的语言前缀 → 2) <html lang> → 3) localStorage 存储值。
 */
export const getCurrentLocale = (): SupportedLocale => {
  if (!isBrowser()) return DEFAULT_LOCALE
  try {
    const routeLocale = parseLocalePath(`${window.location.pathname}${window.location.search}${window.location.hash}`).locale
    if (routeLocale) return normalizeLocale(routeLocale)
  } catch {
    // ignore route failure
  }
  try {
    const docLang = document.documentElement?.lang || ''
    if (docLang.trim()) return normalizeLocale(docLang)
  } catch {
    // ignore dom failure
  }
  return getStoredLocale()
}

/**
 * 拆解路径，剥离其开头的语言前缀。
 * 将路径拆为 pathname/query/hash，规范化多余斜杠，
 * 并返回识别到的语言（无则为 null）及去除前缀后的各部分。
 */
export const stripLocalePrefix = (path: string) => {
  const rawPath = String(path || '').trim() || '/'
  const [pathnameWithMaybeQuery, hash = ''] = rawPath.split('#')
  const [pathnameRaw = '/', query = ''] = pathnameWithMaybeQuery.split('?')
  const pathname = pathnameRaw.startsWith('/') ? pathnameRaw : `/${pathnameRaw}`
  const normalizedPathname = pathname.replace(/\/+/g, '/').replace(/\/+$/g, '') || '/'
  const matched = normalizedPathname.match(LOCALE_PREFIX_RE)
  const matchedLocale = matched?.[1] || ''
  const locale = isSupportedLocale(matchedLocale) ? normalizeLocale(matchedLocale) : null
  const nextPathname = locale
    ? normalizedPathname.slice(matched![0].length) || '/'
    : normalizedPathname
  return {
    locale,
    pathname: nextPathname.startsWith('/') ? nextPathname : `/${nextPathname}`,
    query: query ? `?${query}` : '',
    hash: hash ? `#${hash}` : '',
  }
}

/**
 * 为路径构建带目标语言前缀的本地化路径。
 * 若目标语言等于主语言，则不添加语言前缀（主语言使用裸路径）。
 */
export const buildLocalizedPath = (path: string, locale: unknown, mainLanguage?: unknown): string => {
  const nextLocale = normalizeLocale(locale)
  const parsed = stripLocalePrefix(path)
  const basePath = parsed.pathname === '/' ? '' : parsed.pathname
  const normalizedMainLanguage = resolveMainLanguage(mainLanguage)
  if (normalizedMainLanguage && nextLocale === normalizedMainLanguage) {
    return `${basePath || '/'}${parsed.query}${parsed.hash}`
  }
  return `/${nextLocale}${basePath}${parsed.query}${parsed.hash}`
}

// 解析路径，返回其语言及去除语言前缀后的规范化路径（canonical）
export const parseLocalePath = (path: string) => {
  const parsed = stripLocalePrefix(path)
  return {
    locale: parsed.locale,
    canonicalPath: `${parsed.pathname}${parsed.query}${parsed.hash}`,
    canonicalPathname: parsed.pathname,
  }
}

// 获取指定路径（默认当前地址）的语言作用域：语言及是否含前缀
export const getRouteLocaleScope = (path?: string): RouteLocaleScope => {
  const currentPath = path || (isBrowser()
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : '/')
  const locale = parseLocalePath(currentPath).locale
  return {
    locale,
    hasLocalePrefix: Boolean(locale),
  }
}

// 判断链接是否与当前页面同源（非同源链接不做本地化处理）
const isSameOriginUrl = (href: string) => {
  if (!isBrowser()) return false
  try {
    const url = new URL(href, window.location.origin)
    return url.origin === window.location.origin
  } catch {
    return false
  }
}

// 判断是否应保留原始链接不处理（空链接、锚点、mailto、tel、javascript 协议）
const shouldKeepRawHref = (href: string) => {
  const value = String(href || '').trim()
  return !value || /^(#|mailto:|tel:|javascript:)/i.test(value)
}

// 判断路径是否属于不应加语言前缀的特殊路径（api、静态资源、预览/编辑器等）
const shouldSkipLocalizedPath = (pathname: string) => {
  const normalized = String(pathname || '/').trim() || '/'
  return /^\/(?:api|biz|_nuxt|__nuxt|preview|preview-shell|editor|localPreview|localEditor)(?:\/|$)/i.test(normalized)
}

/**
 * 将单个链接改写为指定语言版本。
 * 仅处理同源、非特殊协议且非跳过路径的链接，其余原样返回。
 */
export const localizeHref = (href: string, locale: unknown, mainLanguage?: unknown): string => {
  if (!isBrowser()) return href
  if (shouldKeepRawHref(href) || !isSameOriginUrl(href)) return href
  try {
    const url = new URL(href, window.location.origin)
    if (shouldSkipLocalizedPath(url.pathname)) return href
    url.pathname = buildLocalizedPath(url.pathname, locale, mainLanguage)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}

/**
 * 依据"当前页面所处语言作用域"解析目标链接。
 * 当前路径带语言前缀时，为目标链接补上相同语言前缀；否则使用裸路径，
 * 从而保证站内跳转维持用户当前所处的语言环境。
 */
export const resolveSiteHref = (href: string, options?: ResolveSiteHrefOptions): string => {
  if (!isBrowser()) return href
  if (shouldKeepRawHref(href) || !isSameOriginUrl(href)) return href
  try {
    const origin = options?.currentOrigin || window.location.origin
    const url = new URL(href, origin)
    if (url.origin !== origin || shouldSkipLocalizedPath(url.pathname)) return href

    const currentPath = options?.currentPath || `${window.location.pathname}${window.location.search}${window.location.hash}`
    const scope = getRouteLocaleScope(currentPath)
    const parsedTarget = stripLocalePrefix(url.pathname)

    url.pathname = scope.hasLocalePrefix && scope.locale
      ? buildLocalizedPath(parsedTarget.pathname, scope.locale, options?.mainLanguage)
      : parsedTarget.pathname

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}

// 解析并跳转到目标站内链接（resolveSiteHref + window.location.assign）
export const navigateSiteHref = (href: string, options?: ResolveSiteHrefOptions) => {
  if (!isBrowser()) return href
  const targetHref = resolveSiteHref(href, options)
  if (!targetHref) return targetHref
  window.location.assign(targetHref)
  return targetHref
}

// 批量处理容器内所有 <a> 链接，按当前语言作用域重写其 href（跳过语言切换按钮）
export const resolveSiteAnchors = (root: ParentNode | null | undefined, options?: ResolveSiteHrefOptions) => {
  if (!root || !isBrowser()) return
  const anchors = root.querySelectorAll?.('a[href]') || []
  for (const anchor of Array.from(anchors) as HTMLAnchorElement[]) {
    if (anchor.hasAttribute('data-lang-trigger')) continue
    const rawHref = anchor.getAttribute('href') || ''
    const resolvedHref = resolveSiteHref(rawHref, options)
    if (resolvedHref && resolvedHref !== rawHref) {
      anchor.setAttribute('href', resolvedHref)
    }
  }
}

// 批量将容器内所有 <a> 链接改写为指定语言版本（跳过语言切换按钮）
export const localizeAnchors = (root: ParentNode | null | undefined, locale: unknown) => {
  if (!root || !isBrowser()) return
  const nextLocale = normalizeLocale(locale)
  const anchors = root.querySelectorAll?.('a[href]') || []
  for (const anchor of Array.from(anchors) as HTMLAnchorElement[]) {
    if (anchor.hasAttribute('data-lang-trigger')) continue
    const rawHref = anchor.getAttribute('href') || ''
    const localizedHref = localizeHref(rawHref, nextLocale)
    if (localizedHref && localizedHref !== rawHref) {
      anchor.setAttribute('href', localizedHref)
    }
  }
}

/**
 * 切换站点语言：
 * 1) 更新 <html lang> 与 localStorage；2) 同步在线客服（sohoChat）语言；
 * 3) storefront 模式下计算目标本地化路径并跳转（preview 模式或 reload=false 不跳转）。
 */
export const setLocale = (value: unknown, options?: { mode?: LocaleRuntimeMode; reload?: boolean; path?: string; mainLanguage?: unknown }) => {
  const locale = normalizeLocale(value)
  applyHtmlLang(locale)
  persistLocale(locale)

  if (!isBrowser()) return locale

  const chatWindow = window as typeof window & { sohoChat?: { setLang?: (lang: string) => void } }
  if (chatWindow.sohoChat?.setLang) {
    try {
      chatWindow.sohoChat.setLang(locale)
    } catch {
      // ignore chat failure
    }
  }

  const mode = options?.mode || 'storefront'
  if (mode === 'preview') return locale

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const targetPath = buildLocalizedPath(options?.path || currentPath, locale, options?.mainLanguage)
  if (options?.reload === false || targetPath === currentPath) return locale
  window.location.assign(targetPath)
  return locale
}

/**
 * 将控制器实例挂载到 window.__cmsLocaleController，
 * 供非 Vue 环境（如 CMS 注入的原生脚本、第三方模板）调用其能力。
 * SSR 环境下返回 null。
 */
export const installLocaleController = () => {
  if (!isBrowser()) return null
  const controller = {
    normalizeLocale,
    configureLocaleController,
    getLocaleMeta,
    getCurrentLocale,
    persistLocale,
    applyHtmlLang,
    buildLocalizedPath,
    parseLocalePath,
    getRouteLocaleScope,
    resolveSiteHref,
    navigateSiteHref,
    resolveSiteAnchors,
    localizeAnchors,
    setLocale,
  }
  ;(window as typeof window & { __cmsLocaleController?: typeof controller }).__cmsLocaleController = controller
  return controller
}